import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';
import { apiClient } from '@/api';

// Member types matching backend API
export interface TeamMember {
  id: string;
  email: string;           // Masked by default (e.g., "mat***@example.com")
  email_preview: string;   // Same as email (masked preview)
  name: string;            // Masked by default (e.g., "Matt B.")
  name_preview: string;    // Same as name (masked preview)
  role: 'owner' | 'admin' | 'member' | 'viewer';
  is_active: boolean;
  is_encrypted: boolean;   // Whether PII is encrypted (reveal available)
  created_at: string;
  updated_at: string;
}

// Response from reveal endpoint
export interface MemberRevealResponse {
  email: string;
  name: string;
}

interface MembersResponse {
  users: TeamMember[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface UpdateMemberRequest {
  name?: string;
  role?: string;
}

interface UseTeamOptions {
  limit?: number;
  offset?: number;
}

interface UseTeamResult {
  members: TeamMember[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  updateMember: (memberId: string, request: UpdateMemberRequest) => Promise<TeamMember>;
  removeMember: (memberId: string) => Promise<void>;
  revealMember: (memberId: string) => Promise<MemberRevealResponse>;
  isUpdating: boolean;
  isRemoving: boolean;
}

export function useTeam(options: UseTeamOptions = {}): UseTeamResult {
  const { session } = useAuth();
  const { tenant } = useTenant();
  const { limit = 50, offset = 0 } = options;
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const queryParams = new URLSearchParams();
  if (limit) queryParams.set('limit', String(limit));
  if (offset) queryParams.set('offset', String(offset));

  const endpoint = `/api/v1/team/members?${queryParams.toString()}`;

  const fetcher = async (url: string): Promise<MembersResponse> => {
    if (!session?.access_token) {
      throw new Error('No authentication token');
    }
    return apiClient.get<MembersResponse>(url, { token: session.access_token });
  };

  const { data, error, isLoading, mutate } = useSWR<MembersResponse>(
    session?.access_token && tenant?.id ? endpoint : null,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 5000,
    }
  );

  const refresh = async () => {
    await mutate();
  };

  const updateMember = useCallback(
    async (memberId: string, request: UpdateMemberRequest): Promise<TeamMember> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsUpdating(true);
      try {
        const response = await apiClient.patch<TeamMember>(
          `/api/v1/team/members/${memberId}`,
          request,
          { token: session.access_token }
        );
        await mutate();
        return response;
      } finally {
        setIsUpdating(false);
      }
    },
    [session?.access_token, mutate]
  );

  const removeMember = useCallback(
    async (memberId: string): Promise<void> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      setIsRemoving(true);
      try {
        await apiClient.delete(`/api/v1/team/members/${memberId}`, {
          token: session.access_token,
        });
        await mutate();
      } finally {
        setIsRemoving(false);
      }
    },
    [session?.access_token, mutate]
  );

  const revealMember = useCallback(
    async (memberId: string): Promise<MemberRevealResponse> => {
      if (!session?.access_token) {
        throw new Error('No authentication token');
      }
      // This call is audit logged on the backend
      const response = await apiClient.get<MemberRevealResponse>(
        `/api/v1/team/members/${memberId}/reveal`,
        { token: session.access_token }
      );
      return response;
    },
    [session?.access_token]
  );

  return {
    members: data?.users ?? [],
    total: data?.total ?? 0,
    hasMore: data?.has_more ?? false,
    isLoading,
    error: error ?? null,
    refresh,
    updateMember,
    removeMember,
    revealMember,
    isUpdating,
    isRemoving,
  };
}

export type { UpdateMemberRequest as UpdateTeamMemberRequest };
